import { useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import type { ChatSession, ChatSessionMessage } from "@KassaroAB/shared/types/chatSession";
import {
  useGetStoreChatSessionsQuery,
  useGetStoreChatSessionMessagesQuery,
  useGetStoreChatSessionChangeRequestQuery,
  useGetStoreChatSessionChangeRequestHistoryQuery,
  useApproveStoreChatSessionChangeRequestMutation,
} from "../query/chatSessionsQuery";
import { NEW_CHAT_STREAM_KEY, useStreamChatSession } from "../hooks/useStreamChatSession";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import { Button } from "../components/Button";
import { ChatSessionSidebar } from "../components/ChatSessionSidebar";
import { ChatDetailPane } from "../components/ChatDetailPane";

const PageRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px);
  height: calc(100dvh - 64px);
  padding: ${spacing.x2}px;
  box-sizing: border-box;
  overflow: hidden;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
  margin-bottom: ${spacing.x2}px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: ${fontSizes.xlarge}px;
  font-weight: 600;
  color: ${colors.text};
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const Split = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
  overflow: hidden;
  background-color: ${colors.background};
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.x4}px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.textMuted};
  text-align: center;
`;

const ErrorBanner = styled.div`
  padding: ${spacing.x2}px ${spacing.x3}px;
  border-radius: ${borderRadius.regular}px;
  background-color: rgba(235, 87, 87, 0.12);
  color: ${colors.danger};
  font-size: ${fontSizes.small}px;
  margin-bottom: ${spacing.x2}px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.x2}px;
`;

const mergeMessages = (
  sessionMessages: ChatSessionMessage[] | undefined,
  streamMessages: ChatSessionMessage[],
): ChatSessionMessage[] => {
  const baseMessages = sessionMessages ?? [];
  const existingIds = new Set(
    baseMessages
      .map((message) => message.id)
      .filter((messageId): messageId is string => messageId !== undefined),
  );
  const pendingMessages = streamMessages.filter(
    (message) => message.id !== undefined && !existingIds.has(message.id),
  );
  return [...baseMessages, ...pendingMessages];
};

export const StoreChatSessionsView = () => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: string }>();
  const sid = storeId as StoreId | undefined;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isNewChat, setIsNewChat] = useState(false);
  const isViewingNewChatRef = useRef(false);

  const { data: sessions = [], isLoading, error, refetch } = useGetStoreChatSessionsQuery(sid);
  const streamKey = isNewChat ? NEW_CHAT_STREAM_KEY : selectedId;

  const {
    data: sessionMessages,
    isLoading: isLoadingMessages,
    error: messagesError,
    refetch: refetchMessages,
  } = useGetStoreChatSessionMessagesQuery(sid, isNewChat ? null : selectedId);

  const {
    data: sessionChangeRequest,
    isLoading: isLoadingChangeRequest,
  } = useGetStoreChatSessionChangeRequestQuery(sid, isNewChat ? null : selectedId);

  const { data: changeRequestHistory } = useGetStoreChatSessionChangeRequestHistoryQuery(
    sid,
    isNewChat ? null : selectedId,
  );

  const {
    sendMessage,
    createSession,
    isStreaming,
    progressSteps,
    streamMessages,
    createdSessions,
    isPendingNewChat,
    error: streamError,
  } = useStreamChatSession(sid, streamKey);

  const { mutate: approveChangeRequest, isPending: isApprovingChangeRequest, error: approveError } =
    useApproveStoreChatSessionChangeRequestMutation(
      sid,
      isNewChat ? null : selectedId,
    );

  const listedSessions = useMemo(() => {
    const sessionsById = new Map<string, ChatSession>();
    for (const session of sessions) {
      sessionsById.set(session.id, session);
    }
    for (const session of createdSessions) {
      if (!sessionsById.has(session.id)) {
        sessionsById.set(session.id, session);
      }
    }
    return Array.from(sessionsById.values()).sort(
      (left, right) =>
        new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
    );
  }, [sessions, createdSessions]);

  const selectedSession = useMemo(() => {
    if (!selectedId || isNewChat) {
      return null;
    }
    return listedSessions.find((session) => session.id === selectedId) ?? null;
  }, [listedSessions, selectedId, isNewChat]);

  const displayMessages = useMemo(
    () =>
      isNewChat
        ? streamMessages
        : mergeMessages(sessionMessages, streamMessages),
    [isNewChat, sessionMessages, streamMessages],
  );

  if (!sid) {
    return (
      <PageRoot>
        <EmptyState>{t("common.loading")}</EmptyState>
      </PageRoot>
    );
  }

  return (
    <PageRoot>
      <PageHeader>
        <Title>{t("storeChatSessions.title")}</Title>
        <Subtitle>{t("storeChatSessions.subtitle")}</Subtitle>
      </PageHeader>
      {error && (
        <ErrorBanner>
          {error instanceof Error ? error.message : t("storeChatSessions.loadFailed")}
          <Button type="button" $color="secondary" onClick={() => refetch()}>
            {t("common.retry")}
          </Button>
        </ErrorBanner>
      )}
      <Split>
        <ChatSessionSidebar
          sessions={listedSessions}
          selectedId={selectedId}
          isNewChat={isNewChat}
          isPendingNewChat={isPendingNewChat}
          isLoading={isLoading}
          onSelect={(id) => {
            isViewingNewChatRef.current = false;
            setSelectedId(id);
            setIsNewChat(false);
          }}
          onNewChat={() => {
            isViewingNewChatRef.current = true;
            setSelectedId(null);
            setIsNewChat(true);
          }}
        />
        {(selectedId || isNewChat) ? (
          <ChatDetailPane
            key={streamKey ?? "empty"}
            session={selectedSession ?? undefined}
            messages={displayMessages}
            isLoadingMessages={isLoadingMessages && !isStreaming}
            messagesError={messagesError}
            onRefetchMessages={refetchMessages}
            onSendMessage={(text, files) => {
              if (isNewChat) {
                createSession(text, files, (session) => {
                  if (!isViewingNewChatRef.current) {
                    return;
                  }
                  isViewingNewChatRef.current = false;
                  setSelectedId(session.id);
                  setIsNewChat(false);
                }).catch(() => undefined);
                return;
              }
              if (selectedId !== null) {
                sendMessage(selectedId, text, files).catch(() => undefined);
              }
            }}
            isSendingMessage={isStreaming}
            sendError={streamError}
            progressSteps={progressSteps}
            changeRequest={sessionChangeRequest}
            changeRequestHistory={changeRequestHistory}
            isLoadingChangeRequest={isLoadingChangeRequest}
            onApproveChangeRequest={() => approveChangeRequest()}
            isApprovingChangeRequest={isApprovingChangeRequest}
            approveError={approveError}
          />
        ) : (
          <EmptyState>{t("storeChatSessions.selectSession")}</EmptyState>
        )}
      </Split>
    </PageRoot>
  );
};
